import {
	faBars,
	faCartShopping,
	faComments,
	faHouse,
	faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { useIsKeyboardLikelyOpen } from '../../hooks/useIsKeyboardLikelyOpen';
import { useCartStore } from '../../stores/cartStore';
import { useChatLauncherStore } from '../../stores/chatLauncherStore';
import { useLoginModalStore } from '../../stores/loginModalStore';
import { useMenuDrawerStore } from '../../stores/menuDrawerStore';
import { usePortalAuthStore } from '../../stores/portalAuthStore';

/**
 * Mobile bottom navigation — Amazon-app pattern.
 *
 * Four tabs: Home, Profile, Cart, Menu (hamburger). Secondary destinations
 * (Orders, Invoices, Account details, Sign out, Theme, Language) live in
 * the slide-in MenuDrawer that the hamburger opens. We deliberately do
 * NOT carry "Shop" as its own tab — the Home route already routes
 * authenticated users into the catalog (and anon visitors with anon-
 * browsing enabled), so a separate Shop tab would be a duplicate jump.
 *
 * Auto-hides while the on-screen keyboard is up. Without this, Android's
 * `position: fixed` BottomNav would stay anchored to `innerHeight` while
 * the keyboard shrinks `visualViewport.height`, leaving the nav behind
 * the keyboard and dead-zoning the bottom of the visible area. Same
 * pattern Amazon, Slack, WhatsApp, Twitter, Reddit native apps use.
 */
export function BottomNav(): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	const display = useDisplaySettings();
	const totalQuantity = useCartStore((s) => s.totalQuantity);
	const openLoginModal = useLoginModalStore((s) => s.open);
	const openMenuDrawer = useMenuDrawerStore((s) => s.open);
	const isMenuOpen = useMenuDrawerStore((s) => s.isOpen);
	const requestOpenChat = useChatLauncherStore((s) => s.requestOpen);
	const hasActiveChat = useChatLauncherStore((s) => s.hasActiveConversation);
	const chatUnread = useChatLauncherStore((s) => s.unreadCount);
	const keyboardOpen = useIsKeyboardLikelyOpen();

	if (keyboardOpen) return null;

	// Cart only renders when the user has an authenticated session AND the
	// storefront permits purchases. Anon visitors still see the other three
	// tabs (Home / Profile / Menu). Profile routes to /account when authed,
	// or pops the login modal when not — same as Amazon's Profile tab.
	const showCart = isAuthenticated && display.canPurchase;
	// Chat requires an authenticated session — without one the rep router
	// has no client to anchor the conversation to. Anon visitors don't see
	// the tab; on desktop the FAB is also gated on the same auth check.
	const showChat = isAuthenticated;

	const isHomeActive =
		location.pathname === '/' ||
		location.pathname === '/catalog' ||
		location.pathname === '/dashboard' ||
		location.pathname.startsWith('/product');
	const isProfileActive = location.pathname.startsWith('/account');
	const isCartActive = location.pathname === '/cart';

	const handleProfile = (): void => {
		if (isAuthenticated) {
			navigate('/account');
		} else {
			openLoginModal();
		}
	};

	return (
		<nav
			className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-brand-primary text-white border-t border-white/10"
			aria-label={t('Primary')}
		>
			<div className="flex items-stretch pb-safe-bottom">
				<TabButton
					icon={faHouse}
					label={t('Home')}
					active={isHomeActive}
					onClick={() => navigate('/')}
				/>
				<TabButton
					icon={faUser}
					label={t('Profile')}
					active={isProfileActive}
					onClick={handleProfile}
				/>
				{showCart && (
					<TabButton
						icon={faCartShopping}
						label={t('Cart')}
						active={isCartActive}
						onClick={() => navigate('/cart')}
						badge={totalQuantity > 0 ? totalQuantity : undefined}
					/>
				)}
				{showChat && (
					<TabButton
						icon={faComments}
						label={t('Chat')}
						active={false}
						onClick={requestOpenChat}
						badge={chatUnread > 0 ? chatUnread : undefined}
						dot={chatUnread === 0 && hasActiveChat}
					/>
				)}
				<TabButton
					icon={faBars}
					label={t('Menu')}
					active={isMenuOpen}
					onClick={openMenuDrawer}
				/>
			</div>
		</nav>
	);
}

interface TabButtonProps {
	icon: typeof faHouse;
	label: string;
	active: boolean;
	onClick: () => void;
	/**
	 * Numeric badge — used by Cart for qty and Chat for unread message
	 * count. Outranks `dot` when both are set; cap rendered at "9+" for
	 * counts >= 10 so the badge stays a fixed-width chip.
	 */
	badge?: number;
	/**
	 * Plain "active" indicator — used by Chat to signal an existing
	 * conversation the user can resume even when there are no unread
	 * messages. Smaller and quieter than `badge` (no number, no
	 * background pill) so it doesn't feel like an alert.
	 */
	dot?: boolean;
}

function TabButton({
	icon,
	label,
	active,
	onClick,
	badge,
	dot,
}: TabButtonProps): JSX.Element {
	const badgeLabel = badge != null && badge >= 10 ? '9+' : badge;
	return (
		<button
			type="button"
			onClick={onClick}
			className={`flex-1 flex flex-col items-center justify-center min-h-[56px] py-2 gap-0.5 relative ${
				active ? 'text-portal-accent' : 'text-white/75 hover:text-white'
			}`}
			aria-label={label}
			aria-current={active ? 'page' : undefined}
		>
			<div className="relative">
				<FontAwesomeIcon icon={icon} className="text-xl" />
				{badge != null ? (
					<span className="absolute -top-1.5 -right-2.5 bg-portal-cart text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5">
						{badgeLabel}
					</span>
				) : dot === true ? (
					<span
						className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-portal-accent ring-2 ring-brand-primary"
						aria-hidden="true"
					/>
				) : null}
			</div>
			<span className="text-[10px] font-medium leading-none">{label}</span>
		</button>
	);
}
